import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, CheckCircle2, ArrowRight, ChevronDown, Coins, Clock } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';

const LitecoinProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const ltcPurchased = invest / buy;
    const revenue = ltcPurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / ltcPurchased;

    setResult({
      ltcPurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'LTC: $60 → $120', buy: '60', sell: '120', invest: '1200' },
    { label: 'LTC: $80 → $200', buy: '80', sell: '200', invest: '2000' },
    { label: 'LTC: $100 → $300', buy: '100', sell: '300', invest: '5000' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'Litecoin (LTC)', slug: 'litecoin-profit-calculator', icon: 'Ł', color: 'gray', current: true },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', color: 'orange', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', color: 'indigo', current: false },
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', color: 'violet', current: false },
    { name: 'XRP', slug: 'xrp-profit-calculator', icon: '✕', color: 'slate', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', color: 'green', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="litecoin-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-500/20 border border-gray-500/30 rounded-lg text-gray-300 hover:bg-gray-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl font-bold">Ł</span>
          <span className="font-medium">Litecoin Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-gray-600/20 text-gray-300 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gray-600/30 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-300">Ł</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Litecoin Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your LTC investment profit — the silver to Bitcoin's gold</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 60"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 120"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 1200"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-gray-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">LTC fees are low (~$0.01-0.05 per transaction)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">LTC Purchased</p>
              <p className="text-xl font-semibold text-gray-300">{result.ltcPurchased.toFixed(4)} LTC</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="LTC"
            toolName="Litecoin Profit Calculator"
            toolSlug="litecoin-profit-calculator"
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-gray-600/10 to-gray-500/10 rounded-xl border border-gray-600/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-gray-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/bitcoin-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-orange-400">Bitcoin Profit</p>
              <p className="text-sm text-gray-400">BTC calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400" />
          </Link>
          <Link to="/tools/ethereum-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400">Ethereum Profit</p>
              <p className="text-sm text-gray-400">ETH calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400" />
          </Link>
          <Link to="/tools/solana-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-violet-400">Solana Profit</p>
              <p className="text-sm text-gray-400">SOL calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-violet-400" />
          </Link>
          <Link to="/tools/xrp-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-slate-400">XRP Profit</p>
              <p className="text-sm text-gray-400">Ripple calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-slate-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Litecoin Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Litecoin Profit Calculator</strong> is a specialized tool for LTC investors to calculate 
              profits from Litecoin trades. Created in 2011 by Charlie Lee (former Google engineer), Litecoin is 
              often called "the silver to Bitcoin's gold" — designed as a faster, lighter version of Bitcoin.
            </p>
            <p>
              Our calculator helps you determine your profit or loss by entering your buy price, sell price, and 
              investment amount. Litecoin's faster block times (2.5 minutes vs Bitcoin's 10 minutes) and lower 
              fees make it practical for everyday transactions and trading.
            </p>
            <p>
              With a fixed supply of 84 million coins (4x Bitcoin's supply) and MimbleWimble privacy features, 
              Litecoin remains one of the most established and widely accepted cryptocurrencies. This calculator 
              helps you track your LTC investment performance accurately.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Litecoin Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your LTC Buy Price', desc: 'Input the price per Litecoin when you purchased. This is your entry price.' },
              { step: 2, title: 'Enter Your LTC Sell Price', desc: 'Input the price at which you sold (or plan to sell) your Litecoin.' },
              { step: 3, title: 'Enter Investment Amount', desc: 'How much USD you invested in LTC. The calculator shows how many Litecoin you bought.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange fees. Litecoin network fees are typically very low (~$0.01-0.05).' },
              { step: 5, title: 'View Your Results', desc: 'See profit/loss, ROI percentage, LTC quantity, and break-even price instantly.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-gray-600/30 text-gray-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Litecoin Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: LTC Trade</h3>
            <div className="space-y-3 text-gray-300">
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $60 per LTC</li>
                <li><strong>Sell Price:</strong> $150 per LTC</li>
                <li><strong>Investment:</strong> $3,000</li>
                <li><strong>Trading Fees:</strong> $15</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">LTC Purchased:</span> <span className="text-gray-300">$3,000 / $60 = 50 LTC</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">50 LTC × $150 = $7,500</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$7,500 - $3,000 = $4,500</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$4,500 - $15 = $4,485</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($4,485 / $3,000) × 100 = 149.5%</span></p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Why is Litecoin called "silver to Bitcoin\'s gold"?', a: 'Litecoin was designed as a lighter, faster version of Bitcoin with 4x the supply (84M vs 21M), 4x faster blocks (2.5 min vs 10 min), and lower fees. It\'s meant for everyday transactions while Bitcoin serves as a store of value.' },
              { q: 'What is Litecoin\'s all-time high?', a: 'Litecoin reached approximately $410 in May 2021. Many long-term holders calculate potential profits based on LTC approaching or exceeding previous highs.' },
              { q: 'Is Litecoin a good investment?', a: 'Litecoin is one of the oldest, most established cryptocurrencies with consistent development and widespread exchange support. However, like all crypto, it\'s volatile. Research thoroughly before investing.' },
              { q: 'What is MimbleWimble on Litecoin?', a: 'MimbleWimble is a privacy protocol activated on Litecoin in 2022. It allows optional confidential transactions, hiding amounts while maintaining Litecoin\'s security and fungibility.' },
              { q: 'How does Litecoin\'s halving affect price?', a: 'Like Bitcoin, Litecoin has halving events every 4 years (reducing mining rewards by 50%). Historically, halvings have preceded price increases due to reduced supply, though past performance doesn\'t guarantee future results.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-gray-600/10 to-blue-500/10 rounded-lg border border-gray-600/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <Clock size={16} className="text-gray-400" />
          Litecoin Speed Advantage
        </h4>
        <p className="text-sm text-gray-400">
          Litecoin processes blocks every 2.5 minutes (vs Bitcoin's 10 minutes), meaning faster confirmations 
          and lower fees. This makes LTC more practical for trading and everyday payments while maintaining 
          Bitcoin's proven security model.
        </p>
      </div>
    </div>
  );
};

export default LitecoinProfitCalculatorTool;
